import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import StaticPageHeader from '../components/common/StaticPageHeader';
import { callPostWebservice } from '../hooks/api';
import useMerchantSettings from '../hooks/useMerchantSettings';
import CustomInput from '../components/common/CustomInput';
import { Helmet } from 'react-helmet-async';
import '../assets/css/allpages.css';

const validationSchema = Yup.object({
    name: Yup.string().required("Your Name is required"),
    email: Yup.string()
        .email("Invalid email address")
        .required("Your Email is required"),
    phone_number: Yup.string()
        .matches(/^[0-9\s+]+$/, 'Phone number must contain only digits')
        .min(10, 'Phone number must be at least 10 digits')
        .required('Phone number is required'),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string().required("Message is required"),
});

const ContactUs = () => {
    const { merchantSettings } = useMerchantSettings();

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            phone_number: "",
            subject: "",
            message: "",
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            callPostWebservice('contact-us', values, (response) => {
                setSubmitting(false);
                resetForm();
                alert('Submit Data Successfully');
            }, (error) => {
                setSubmitting(false);
                formik.setErrors({ email: error.errMessage });
            });
        },
    });

    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const storeName = merchantSettings?.legal_name || 'Online Store';

    return (
        <div className="static-page-wrapper" style={{ backgroundColor: '#f9f9f9' }}>
            <Helmet>
                <title>{`Contact Us | ${storeName}`}</title>
                <meta name="description" content={`Contact ${storeName} for customer support, inquiries, or feedback. We are here to help.`} />
                {currentOrigin && <link rel="canonical" href={`${currentOrigin}/contactus`} />}
            </Helmet>
            <StaticPageHeader title="Contact Us" />

            <div className="container static-page-container py-5">
                <div className="row justify-content-center mb-5">
                    <div className="col-xl-8 col-lg-10 col-md-12 text-center">
                        <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem', color: '#333' }}>Get in Touch</h2>
                        <p className="text-muted fs-5">
                            We'd love to hear from you. Fill out the form and our team will get back to you shortly.
                        </p>
                    </div>
                </div>

                <div className="row justify-content-center align-items-stretch g-4 mx-auto" style={{ maxWidth: '1100px' }}>
                    
                    {/* Contact Information Side */}
                    <div className="col-xl-4 col-lg-5 col-md-12">
                        <div className="p-5 bg-dark h-100 rounded shadow-sm text-white">
                            <h4 className="fw-bold mb-4">Contact Information</h4>
                            <p className="mb-5 text-light" style={{ opacity: 0.8 }}>
                                Have questions? Reach out to us through any of the following channels.
                            </p>
                            
                            <div className="flex-grow-1">
                                {merchantSettings?.merchant_configuration?.contact_us ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: merchantSettings.merchant_configuration.contact_us
                                        }}
                                        className="contact-info-custom custom-html-content text-light"
                                    />
                                ) : (
                                    <div className="d-flex flex-column gap-4">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light bg-opacity-10 rounded d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '45px', height: '45px' }}>
                                                <i className="fas fa-phone-alt fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-bold">Phone</h6>
                                                <span className="text-light" style={{ opacity: 0.8 }}>+1 (555) 123-4567</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light bg-opacity-10 rounded d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '45px', height: '45px' }}>
                                                <i className="fas fa-envelope fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-bold">Email</h6>
                                                <span className="text-light" style={{ opacity: 0.8 }}>support@example.com</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light bg-opacity-10 rounded d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '45px', height: '45px' }}>
                                                <i className="fas fa-map-marker-alt fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-bold">Location</h6>
                                                <span className="text-light" style={{ opacity: 0.8 }}>123 Commerce St, NY 10001</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Contact Form Side */}
                    <div className="col-xl-8 col-lg-7 col-md-12">
                        <div className="p-5 bg-white h-100 rounded shadow-sm border">
                            <form onSubmit={formik.handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <CustomInput
                                            label="First Name *"
                                            name="name"
                                            placeholder="John Doe"
                                            formik={formik}
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <CustomInput
                                            label="Email Address *"
                                            name="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            formik={formik}
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <CustomInput
                                            label="Phone Number *"
                                            name="phone_number"
                                            placeholder="+1 (555) 000-0000"
                                            formik={formik}
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <CustomInput
                                            label="Subject *"
                                            name="subject"
                                            placeholder="How can we help?"
                                            formik={formik}
                                        />
                                    </div>

                                    <div className="col-12 mb-3">
                                        <CustomInput
                                            label="Message *"
                                            name="message"
                                            as="textarea"
                                            rows="5"
                                            placeholder="Write your message here..."
                                            formik={formik}
                                        />
                                    </div>

                                    <div className="col-12 mt-4 text-end">
                                        <button
                                            type="submit"
                                            className="btn btn-dark px-5 py-2"
                                            disabled={formik.isSubmitting}
                                            style={{ fontWeight: '600' }}
                                        >
                                            {formik.isSubmitting ? (
                                                <><i className="fas fa-spinner fa-spin me-2"></i> Sending...</>
                                            ) : (
                                                <>Send Message</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;